/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useMemo } from "react"
import { CheckmarkTiny } from "@duxton-web/icons"
import { Button, Space, Timeline, Typography } from "@duxton-web/ui"
import classNames from "classnames"

import { useTranslationsMap } from "common/utils/i18n"

import {
  RETURN_CONTEXTUAL_ACTION,
  RETURN_OVERFLOW_DEEPLINK,
  RETURN_TIMELINE_STATE,
  ReturnContextualAction,
  ReturnTimelineEntry,
  ReturnTimelineState,
  getReturnContextualActionLabel,
  getReturnTimelineAttributionLabel,
  getReturnTimelineCountLabel,
  getReturnTimelineLabel,
} from "ecommerce/constants/returnRefund"
import {
  toMexTrackingDeeplink,
  toTrackingHref,
} from "ecommerce/utils/returnRefundHelpers"
import { isWebview } from "utils/webview"

import styles from "./ReturnStatusTimeline.modules.css"

const { Text } = Typography

const STATE_CLASS: Record<ReturnTimelineState, string> = {
  [RETURN_TIMELINE_STATE.complete]: styles.complete,
  [RETURN_TIMELINE_STATE.current]: styles.current,
  [RETURN_TIMELINE_STATE.upcoming]: styles.upcoming,
}

/**
 * `Contact Grab support` never carries a BE destination (that is exactly what
 * classifies it as this action, see `toTimelineAction` — `href` is guaranteed
 * `undefined` for it, so `toTrackingHref`/`toMexTrackingDeeplink` would no-op on
 * it anyway), so only the IN-APP case needs a special rule: it reuses the header
 * overflow's own `Contact Grab` deeplink as a real anchor instead of a per-step
 * link. On desktop it falls through to the `onAction` button unresolved, which
 * opens the same `ContactGrabUnavailableModal` the overflow menu does.
 */
const resolveActionHref = (
  action: ReturnContextualAction | undefined,
  href: string | undefined,
  inApp: boolean,
): string | undefined => {
  if (inApp && action === RETURN_CONTEXTUAL_ACTION.contactGrabSupport) {
    return RETURN_OVERFLOW_DEEPLINK.contactGrab
  }

  return inApp ? toMexTrackingDeeplink(href) : toTrackingHref(href)
}

interface StepDotProps {
  state: ReturnTimelineState
}

const StepDot: FC<StepDotProps> = ({ state }) => (
  <span className={classNames(styles.dot, STATE_CLASS[state])}>
    {state === RETURN_TIMELINE_STATE.complete && (
      <CheckmarkTiny className={styles.dotIcon} />
    )}
  </span>
)

interface ReturnStatusTimelineProps {
  /**
   * Steps to render. Step count and labels vary by outcome — three for a closed
   * auto-refund, five for an open request — so nothing here assumes a fixed set.
   */
  entries: ReturnTimelineEntry[]
  /**
   * The FALLBACK hand-off only. An action whose destination resolves to a link
   * — `Track here` with BE's tracking deeplink, or `Contact Grab support` in
   * the app (`resolveActionHref` reuses the header overflow's own deeplink) —
   * renders as a real anchor and never calls this. It fires for whatever is
   * still unresolved: `Contact Driver`, a malformed deeplink, or `Contact Grab
   * support` on DESKTOP only, where the caller opens `ContactGrabUnavailableModal`
   * rather than reaching the host. Side effects that must run on EVERY action
   * click (analytics, mark-as-viewed) do not belong here.
   */
  onAction(action: ReturnContextualAction, href?: string): void
}

export const ReturnStatusTimeline: FC<ReturnStatusTimelineProps> = ({
  entries,
  onAction,
}) => {
  const text = useTranslationsMap({
    actionContactDriver: "ecommerce.sellerCentre.returns.actionContactDriver",
    actionContactGrabSupport:
      "ecommerce.sellerCentre.returns.actionContactGrabSupport",
    actionTrackHere: "ecommerce.sellerCentre.returns.actionTrackHere",
    attributionReviewedByGrab:
      "ecommerce.sellerCentre.returns.attributionReviewedByGrab",
    timelineItemDelivered:
      "ecommerce.sellerCentre.returns.timelineItemDelivered",
    timelinePendingAcknowledgement:
      "ecommerce.sellerCentre.returns.timelinePendingAcknowledgement",
    timelinePendingInspection:
      "ecommerce.sellerCentre.returns.timelinePendingInspection",
    timelinePendingReturn:
      "ecommerce.sellerCentre.returns.timelinePendingReturn",
    timelineRefundApproved:
      "ecommerce.sellerCentre.returns.timelineRefundApproved",
    timelineRejectedCount:
      "ecommerce.sellerCentre.returns.timelineRejectedCount",
    timelineRequestAcknowledged:
      "ecommerce.sellerCentre.returns.timelineRequestAcknowledged",
    timelineRequestCanceled:
      "ecommerce.sellerCentre.returns.timelineRequestCanceled",
    timelineRequestRejected:
      "ecommerce.sellerCentre.returns.timelineRequestRejected",
    timelineRequestSubmitted:
      "ecommerce.sellerCentre.returns.timelineRequestSubmitted",
    timelineReturnInTransit:
      "ecommerce.sellerCentre.returns.timelineReturnInTransit",
  })
  // `useTranslationsMap` returns a new `text` object every render, so a
  // `useMemo` keyed on it would recompute every render anyway — these factories
  // are cheap lookups over a handful of enum values, not worth the useMemo
  // wrapper's own overhead for zero caching benefit.
  const timelineLabel = getReturnTimelineLabel(text)
  // `timelineRejectedCount` comes back as the raw PhraseApp template (the literal
  // `<number>` token still in it) — the factory wants the interpolated closure
  // its type declares, so that substitution happens here, at the call site.
  // This one DOES stay memoized: `text.timelineRejectedCount` is a stable
  // string across renders, unlike `text` itself.
  const timelineCountLabel = useMemo(
    () =>
      getReturnTimelineCountLabel({
        timelineRejectedCount: count =>
          text.timelineRejectedCount.replace("<number>", String(count)),
      }),
    [text.timelineRejectedCount],
  )
  const attributionLabel = getReturnTimelineAttributionLabel(text)
  const contextualActionLabel = getReturnContextualActionLabel(text)

  return (
    <Timeline
      className={styles.timeline}
      items={entries.map((entry, index) => {
        // Falsy guard on purpose: a mechanical `count: 0` must not render `0 Rejected`.
        const countLabel = entry.count
          ? timelineCountLabel[entry.step]?.(entry.count)
          : undefined
        // A const so the narrowing survives into the onClick closure — and a step
        // can legitimately appear twice (e.g. a retried delivery), hence the
        // index-composite key.
        const action = entry.action
        /*
         * BE sends the CONSUMER app's deeplink
         * (`grab://open?screenType=EXPRESS_WEB_TRACKING&page=<https URL>`), which
         * neither host can open verbatim. The webview re-wraps it for the merchant
         * app's own scheme (`grabmex://EXPRESS?url=…`) so the app's URL interception
         * routes it; every other host extracts the tracking page and opens it as an
         * ordinary web page. Unresolvable values fall back to the `onAction` button.
         *
         * `Contact Grab support` is resolved separately — see `resolveActionHref`.
         */
        const inApp = isWebview()
        const actionHref = resolveActionHref(action, entry.actionHref, inApp)

        return {
          key: `${entry.step}-${index}`,
          className: STATE_CLASS[entry.state],
          dot: <StepDot state={entry.state} />,
          children: (
            <Space direction="vertical" size={2}>
              <div className={styles.labelRow}>
                <Text strong className={styles.label}>
                  {/* The server renders the timeline copy (the contract forbids deriving
                      it client-side), so its title wins; the label map serves mock-backed
                      entries, and the raw code is the last resort. */}
                  {entry.title ?? timelineLabel[entry.step] ?? entry.step}
                </Text>
                {countLabel && (
                  <Text type="secondary" strong className={styles.small}>
                    {countLabel}
                  </Text>
                )}
              </div>
              {entry.detail && (
                <Text type="secondary" className={styles.small}>
                  {entry.detail}
                </Text>
              )}
              {entry.attribution && (
                <Text type="secondary" className={styles.small}>
                  {attributionLabel[entry.attribution]}
                </Text>
              )}
              {action && actionHref && inApp && (
                /*
                 * A real anchor, not an onClick: WKWebView reports a link click to
                 * the app as `.linkActivated` and a scripted `location.href` as
                 * `.other`, and the app's URL interception keys on the former. No
                 * `target` — a scheme must stay in this document, or the browser
                 * strands an empty tab on the deeplink URL.
                 */
                <Button
                  type="link"
                  className={styles.actionLink}
                  href={actionHref}
                >
                  {entry.actionLabel ?? contextualActionLabel[action]}
                </Button>
              )}
              {action && actionHref && !inApp && (
                /*
                 * The extracted page as an ordinary navigation — so this one DOES
                 * take a new tab, otherwise the merchant loses the return request
                 * they were reading.
                 */
                <Button
                  type="link"
                  className={styles.actionLink}
                  href={actionHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {entry.actionLabel ?? contextualActionLabel[action]}
                </Button>
              )}
              {action && !actionHref && (
                // A real button so the hand-off is focusable and keyboard-operable —
                // a Link without href never enters the tab order.
                <Button
                  type="link"
                  className={styles.actionLink}
                  onClick={() => onAction(action, entry.actionHref)}
                >
                  {entry.actionLabel ?? contextualActionLabel[action]}
                </Button>
              )}
            </Space>
          ),
        }
      })}
    />
  )
}
